import { Controller } from '@hotwired/stimulus';
import { Modal } from 'flowbite'
import { animate } from 'motion'

export default class extends Controller {
  connect() {
    // Store the turbo-frame ID and classes for restoring after close
    const turboFrame = this.element.querySelector('turbo-frame')
    this.frameId = turboFrame?.id || 'modal'
    this.frameClasses = turboFrame?.className || ''

    const options = {
      placement: 'center-center',
      backdrop: 'static',
      backdropClasses: 'modal-backdrop opacity-0 bg-dark-backdrop/50 fixed inset-0 z-40',
      closable: false,
      onShow: () => {
        this.animateIn()
      }
    }
    this.modal = new Modal(this.element, options)

    // Observe the modal element (not the turbo-frame directly) for when the frame
    // is emptied by a turbo_stream.update. We observe the parent so the observer
    // survives dismissModal() recreating the turbo-frame element.
    this.observer = new MutationObserver(() => {
      const frame = this.element.querySelector('turbo-frame')
      if (frame && frame.innerHTML.trim() === '' && this.modal.isVisible()) {
        this.dismissModal()
      }
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  open() {
    if (this.modal.isVisible()) {
      return;
    }
    this.modal.show()
  }

  async close(event) {
    // Don't close modal for turbo_stream responses — these are handled by
    // the MutationObserver when the frame is emptied via turbo_stream.update
    const fetchResponse = event.detail?.fetchResponse
    if (fetchResponse) {
      const contentType = fetchResponse.response?.headers?.get('Content-Type') || ''
      if (contentType.includes('text/vnd.turbo-stream.html')) return
    }

    if (!fetchResponse || fetchResponse.succeeded) {
      await this.dismissModal()
    }
  }

  async dismissModal() {
    try {
      await this.animateOut()
    } catch (e) {
      // Animation may fail if backdrop/element already removed by turbo stream
    }
    try {
      this.modal.hide()
    } catch (e) {
      // Modal instance may be stale
    }
    // Force-remove any leftover backdrops regardless of above outcomes
    document.querySelectorAll('.modal-backdrop, [modal-backdrop]').forEach(el => el.remove())
    this.element.innerHTML = `<turbo-frame id="${this.frameId}" class="${this.frameClasses}"></turbo-frame>`
  }

  animateIn() {
    // Get backdrop and modal content elements
    const backdrop = document.querySelector('.modal-backdrop')
    animate(
      backdrop,
      { opacity: [0, 1] },
      { duration: 0.3, easing: 'ease-out' }
    )

    // Animate modal zoom in and fade in
    this.element.style.transform = 'scale(0.95)'
    animate(
      this.element,
      { opacity: [0, 1], transform: ['scale(0.95)', 'scale(1)'] },
      { duration: 0.2, easing: 'ease-out' }
    )
  }

  async animateOut() {
    // Get backdrop and modal elements
    const backdrop = document.querySelector('.modal-backdrop')

    // Animate both backdrop and modal out simultaneously
    const animations = []

    // Fade out backdrop
    if (backdrop) {
      animations.push(
        animate(
          backdrop,
          { opacity: [1, 0] },
          { duration: 0.2, easing: 'ease-in' }
        ).finished
      )
    }

    // Zoom out and fade out modal
    animations.push(
      animate(
        this.element,
        {
          opacity: [1, 0],
          transform: ['scale(1)', 'scale(0.95)']
        },
        { duration: 0.2, easing: 'ease-in' }
      ).finished
    )

    // Wait for all animations to complete
    await Promise.all(animations)
  }
}
